import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Papa } from 'ngx-papaparse';
import { SECONDS } from 'src/app/shared/functions/time';
import { PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER } from 'src/app/shared/providers';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';

import { P95UtilizationListComponent } from './components/p95-utilization-list/p95-utilization-list.component';
import { P95UtilizationService } from './services/p95-utilization/p95-utilization.service';
import { IP95UtilizationService } from './services/p95-utilization/p95-utilization.service.types';

interface DurationOption {
  label: '1 Week' | '1 Month' | '1 Quarter';
  value: '1w' | '1m' | '3m';
}

interface P95CsvDataRow {
  Location: string;
  Link: string;
  'In %': string;
  'Out %': string;
  'Speed In (Mbps)': string;
  'Speed Out (Mbps)': string;
}

const DURATION_OPTIONS: ReadonlyArray<DurationOption> = Object.freeze([
  { label: '1 Week', value: '1w' },
  { label: '1 Month', value: '1m' },
  { label: '1 Quarter', value: '3m' },
]);

@Component({
  standalone: true,
  selector: 'app-p95-utilization-page',
  templateUrl: './p95-utilization-page.component.html',
  styleUrl: './p95-utilization-page.component.scss',
  imports: [
    P95UtilizationListComponent,
    MatPaginatorModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
  ],
  providers: PAGE_LABEL_MAT_PAGINATOR_INTL_PROVIDER,
})
export class P95UtilizationPageComponent implements OnInit, OnDestroy {
  public readonly observations$ = inject<IP95UtilizationService>(
    P95UtilizationService,
  );

  private readonly router$ = inject(Router);

  private readonly activatedRoute$ = inject(ActivatedRoute);

  private readonly papa$ = inject(Papa);

  public readonly durationOptions = DURATION_OPTIONS;

  public readonly selectedDuration$ = signal<'1w' | '1m' | '3m'>('1m');

  public readonly searchTerm$ = signal<string>('');

  public readonly pageIndex$ = signal<number>(0);

  public readonly pageSize$ = signal<number>(10);

  public readonly csvFileName = 'P95-Utilization.csv';

  public readonly filteredItemsCount$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const searchTerm = this.searchTerm$().trim();

    if (!searchTerm) {
      return records.length;
    }

    return records.filter((record) => objectContainsTerm(record, searchTerm))
      .length;
  });

  public readonly filteredRecordsForCsv$ = computed(() => {
    const records = this.observations$.records$() ?? [];
    const searchTerm = this.searchTerm$().trim();

    if (!searchTerm) {
      return records;
    }

    return records.filter((record) => objectContainsTerm(record, searchTerm));
  });

  public readonly csvData$ = computed<P95CsvDataRow[]>(() =>
    this.filteredRecordsForCsv$().map((record) => ({
      Location: record.name_customer_location ?? '',
      Link: record.dim_connection_id ?? '',
      'In %': this.formatPercentage(record.p95_utilization_rx_pct),
      'Out %': this.formatPercentage(record.p95_utilization_tx_pct),
      'Speed In (Mbps)': this.formatSpeed(record.provisioned_speed_rx_mbps),
      'Speed Out (Mbps)': this.formatSpeed(record.provisioned_speed_tx_mbps),
    })),
  );

  public readonly filtersChangeEffect = effect(() => {
    // Duration changes trigger data reload from API.
    this.selectedDuration$();
    this.pageIndex$.set(0);
    this.getPage({
      // Restarts page when new filters are applied
      debounce: SECONDS(1),
    });
  });

  public readonly searchPageResetEffect = effect(() => {
    this.searchTerm$();
    this.pageIndex$.set(0);
  });

  ngOnInit(): void {
    this.applyQueryParams(this.activatedRoute$.snapshot.queryParams);
    this.queryParamsSubscription = this.activatedRoute$.queryParams.subscribe(
      (params) => this.applyQueryParams(params),
    );
  }

  ngOnDestroy(): void {
    clearTimeout(this.debouncedGetPage);
    this.queryParamsSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex$.set(event.pageIndex);
    this.pageSize$.set(event.pageSize);
  }

  private debouncedGetPage: ReturnType<typeof setTimeout> | undefined;

  private queryParamsSubscription: import('rxjs').Subscription | undefined;

  private getPage(options: { debounce?: number }): void {
    clearTimeout(this.debouncedGetPage);
    this.debouncedGetPage = setTimeout(
      () => {
        this.observations$.getPage({
          duration: this.selectedDuration$(),
        });
      },
      this.debouncedGetPage === undefined ? 0 : (options.debounce ?? 0),
    );
  }

  private applyQueryParams(params: Params): void {
    const durationParam = (params.duration ?? '') as string;
    if (
      (durationParam === '1w' ||
        durationParam === '1m' ||
        durationParam === '3m') &&
      durationParam !== this.selectedDuration$()
    ) {
      this.selectedDuration$.set(durationParam);
    }

    const searchParam = (params.search ?? '') as string;
    if (typeof searchParam === 'string' && searchParam !== this.searchTerm$()) {
      this.searchTerm$.set(searchParam);
    }
  }

  public readonly urlSyncEffect = effect(() => {
    const duration = this.selectedDuration$();
    const search = (this.searchTerm$() || '').trim();

    this.router$.navigate([], {
      relativeTo: this.activatedRoute$,
      queryParamsHandling: 'merge',
      replaceUrl: true,
      queryParams: {
        duration,
        search: search || undefined,
      },
    });
  });

  public exportToCsv(): void {
    const csvUnparse = this.papa$.unparse(
      this.csvData$() as unknown as Array<Record<string, string>>,
    );
    const csvBlob = new Blob([csvUnparse], { type: 'text/csv;charset=utf-8;' });
    const csvURL = window.URL.createObjectURL(csvBlob);
    const tempLink = document.createElement('a');
    tempLink.href = csvURL;
    tempLink.setAttribute('download', this.csvFileName);
    tempLink.click();
    window.URL.revokeObjectURL(csvURL);
  }

  private formatPercentage(value: number | null | undefined): string {
    if (value == null) return '';
    return `${value.toFixed(2)}%`;
  }

  private formatSpeed(value: number | null | undefined): string {
    if (value == null) return '';
    return `${value.toFixed(2)}`;
  }
}
